import {
  type QueryClient,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";
import { getApiErrorStatus } from "@/lib/apiErrors";
import { usersApi } from "@/lib/humhub-api/users";
import type { User } from "@/lib/humhub-api/types";

// Query Keys for user by GUID
export const userByGuidQueryKeys = {
  all: ["users", "byGuid"] as const,
  detail: (guid: string) => [...userByGuidQueryKeys.all, guid] as const,
  notFound: (guid: string) =>
    [...userByGuidQueryKeys.all, "notFound", guid] as const,
};

const USER_BY_GUID_NOT_FOUND_TTL_MS = 5 * 60 * 1000;

const isUserGuidKnownMissing = (
  queryClient: QueryClient,
  guid: string,
): boolean => {
  const expiresAt = queryClient.getQueryData<number>(
    userByGuidQueryKeys.notFound(guid),
  );
  if (!expiresAt) return false;

  if (Date.now() > expiresAt) {
    queryClient.removeQueries({
      queryKey: userByGuidQueryKeys.notFound(guid),
      exact: true,
    });
    return false;
  }

  return true;
};

const markUserGuidKnownMissing = (queryClient: QueryClient, guid: string) => {
  queryClient.setQueryData(
    userByGuidQueryKeys.notFound(guid),
    Date.now() + USER_BY_GUID_NOT_FOUND_TTL_MS,
  );
};

export const useUserByGuid = () => {
  const queryClient = useQueryClient();

  // ユーザー情報をGUIDで取得する関数（キャッシュ付き）
  const fetchUserByGuid = useCallback(
    async (guid: string): Promise<User> => {
      if (isUserGuidKnownMissing(queryClient, guid)) {
        throw new Error("User not found");
      }

      try {
        return await queryClient.fetchQuery({
          queryKey: userByGuidQueryKeys.detail(guid),
          queryFn: () => usersApi.getUserByGuid(guid),
          staleTime: 5 * 60 * 1000,
          gcTime: 10 * 60 * 1000,
          retry: false,
        });
      } catch (err: unknown) {
        if (getApiErrorStatus(err) === 404) {
          markUserGuidKnownMissing(queryClient, guid);
        }
        console.error("Error fetching user by GUID:", err);
        throw new Error(
          typeof err === "object" && err !== null && "message" in err
            ? (err.message as string)
            : "Failed to load user information",
        );
      }
    },
    [queryClient],
  );

  return {
    fetchUserByGuid,
  };
};

// GUIDでユーザー情報を取得するフック（React Queryを使用）
export const useUserByGuidQuery = (guid: string | null, enabled = true) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: userByGuidQueryKeys.detail(guid ?? ""),
    queryFn: async () => {
      if (!guid) {
        throw new Error("User GUID is required");
      }
      if (isUserGuidKnownMissing(queryClient, guid)) {
        throw new Error("User not found");
      }
      try {
        return await usersApi.getUserByGuid(guid);
      } catch (err) {
        if (getApiErrorStatus(err) === 404) {
          markUserGuidKnownMissing(queryClient, guid);
        }
        throw err;
      }
    },
    enabled: enabled && guid !== null,
    staleTime: 5 * 60 * 1000, // 5分間新鮮
    gcTime: 10 * 60 * 1000, // 10分間キャッシュ保持
    retry: false,
  });
};
